import React, { useState, useEffect } from "react";
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableRow, Paper, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { theme } from "../../../Theme";
import { getUrl } from "src/Utils/Common";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import CustomSelectBox from "src/Components/shared/shared/CustomSelectBox";

const getContributionColor = (value) => {
  if (value > 0) return theme.palette.success.main;
  if (value < 0) return theme.palette.error.main;
  return "#bdbdbd";
};

const formatContribution = (value) =>
  value > 0 ? `+${value}%` : `${value}%`;

function MarketContributionSegments({
  selectedHierarchy: propSelectedHierarchy,
  setSelectedHierarchy: propSetSelectedHierarchy,
  onDataLoaded,
} = {}) {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'lg'));
  const needsScroll = useMediaQuery('(max-width:600px)');
  
  const projectId = useSelector((state) => state.userProject.projectId);
  const [segmentData, setSegmentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [hierarchyOptions, setHierarchyOptions] = useState([]);
  const [localSelectedHierarchy, setLocalSelectedHierarchy] = useState("brand");
  const selectedHierarchy =
    propSelectedHierarchy !== undefined ? propSelectedHierarchy : localSelectedHierarchy;
  const setSelectedHierarchy =
    propSetSelectedHierarchy !== undefined ? propSetSelectedHierarchy : setLocalSelectedHierarchy;
  const [loadingHierarchy, setLoadingHierarchy] = useState(true);

  useEffect(() => {
    function fetchHierarchyLevels() {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        setError("Project ID not available");
        setLoadingHierarchy(false);
        return;
      }
      setLoadingHierarchy(true);
      setError(null);
      fetch(`${getUrl()}/market_overview/hierarchy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("userId") || "",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
        body: JSON.stringify({ project_id: projectId }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => {
          console.log("result from hierarchy", result);

          const hierarchyData =
            result?.["0"] ||
            result?.Product ||
            result?.product ||
            {};

          if (Object.keys(hierarchyData).length === 0) {
            throw new Error("No hierarchy data available");
          }
          
          const allowedHierarchyLevels = ["brand", "class", "manufacturer", "segment"];
          const rawHierarchyLevels = Object.keys(hierarchyData);
          const hierarchyLevels = rawHierarchyLevels.filter((key) =>
            allowedHierarchyLevels.includes(key)
          );
          const finalHierarchyOptions = [
            ...hierarchyLevels,
            "channel",
            "retailer"
          ];
          setHierarchyOptions(finalHierarchyOptions);
          // When controlled by parent (e.g. Canvas), don't overwrite — parent owns the value
          if (propSetSelectedHierarchy === undefined) {
            setSelectedHierarchy((prev) => {
              if (!prev || !hierarchyLevels.includes(prev)) {
                return hierarchyLevels.includes("brand")
                  ? "brand"
                  : hierarchyLevels[0] || "";
              }
              return prev;
            });
          }
          setError(null);
        })
        .catch((error) => {
          console.error("Error fetching hierarchy levels:", error);
          setError(error.message || "Failed to fetch hierarchy levels");
        })
        .finally(() => {
          setLoadingHierarchy(false);
        });
    }

    fetchHierarchyLevels();
  }, [projectId]);

    const fetchMarketContributionData = async () => {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        setError("Project ID not available");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await fetch(
          `${getUrl()}/market_overview/market_contribution?project_id=${projectId}&time_period=P12M&filters={}&hierarchy=${selectedHierarchy}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-access-token": localStorage.getItem("userId") || "",
              "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
            },
          }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
               
        const hierarchyField = selectedHierarchy;
        
        if (!data || !data[hierarchyField] || !Array.isArray(data[hierarchyField])) {
          throw new Error(`Invalid data structure: ${hierarchyField} array not found`);
        }
        
        if (!data["wob_%"] || !data["Contribution to Growth"] || !data.value_sales || !data.value_sales_iya) {
          throw new Error('Missing required data fields in API response');
        }
        
        const transformedData = data[hierarchyField].map((name, index) => ({
          name: name,
          wob: (data["wob_%"][index] || 0) * 100, 
          contribution: data["Contribution to Growth"][index] || 0,
          valueSales: (data.value_sales[index] || 0) / 1000000, 
          iva: data.value_sales_iya[index] || 0
        }));
        
        setSegmentData(transformedData);
        setError(null);
        if (onDataLoaded) onDataLoaded(transformedData);
      } catch (err) {
        console.error('Error fetching market contribution data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchMarketContributionData();
  }, [projectId, selectedHierarchy]);

  const handleRefresh = () => {
    setSegmentData([]);
    setError(null);
    fetchMarketContributionData();
  };

  const isLoading = loading || loadingHierarchy;

  const barAreaHeight = isMobile ? 120 : 160;
  const mobileColWidth = 30;
  const fitColWidth = isTablet ? 36 : 70;
  const barWidth = isMobile ? 16 : isTablet ? 24 : 32;
  const maxWob = segmentData.length > 0 ? Math.max(...segmentData.map((segment) => segment.wob)) || 1 : 1;
  
  const minColumnWidth = needsScroll ? `${mobileColWidth}px` : isTablet ? '70px' : '70px';
  const gridColumns = needsScroll
    ? `repeat(${segmentData.length}, minmax(${minColumnWidth}, 1fr))`
    : `repeat(${segmentData.length}, minmax(0, 1fr))`;
  const scrollableMinWidth = segmentData.length * (needsScroll ? mobileColWidth : fitColWidth) + (needsScroll ? 40 : 60);


  const tableRows = [
    {
      label: "Value Sales",
      getValue: (segment) => `${segment.valueSales.toFixed(1)}M SAR`,
      leftCellStyles: {
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
      },
      cellStyles: {
        backgroundColor: "#f7f7f7",
        color: theme.palette.text.primary,
      },
    },
    {
      label: "IYA",
      getValue: (segment) => `${segment.iva.toFixed(1)}%`,
      leftCellStyles: {
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
      },
      cellStyles: {
        backgroundColor: "#f7f7f7",
      },
      valueColor: (segment) =>
        segment.iva < 100
          ? theme.palette.error.main
          : theme.palette.success.main,
    },
  ];

  return (
    <Box
      sx={{
        width: { xs: "70%", sm: "100%", md: "95%", lg: "90%" },
        maxWidth: "100%",
        minWidth: 0,
        backgroundColor: "#fff",
        borderRadius: 2,
        p: { xs: 1, sm: 1.5, md: 2, lg: 3 },
        margin: "0 auto",
        overflowX: "hidden",
        overflowY: "hidden",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.15rem" },
          px: { xs: 0.5, sm: 0.75, md: 1 },
          color: theme.palette.primary.main,
          textAlign: "center",
          lineHeight: 1,
          mb: 2,
        }}
      >
        Market Contribution by Segments/Retailer
      </Typography>

      <Box
        display="flex"
        justifyContent="center"
        mb={1}
        px={{ xs: 1, sm: 0 }}
      >
        <CustomSelectBox
          sx={{
            fontWeight: 500,
            fontSize: {
              xs: "14px",
              sm: "16px",
              md: "18px",
            },
            height: {
              xs: 32,
              sm: 36,
              md: 40,
            },
            minHeight: {
              xs: 32,
              sm: 36,
              md: 40,
            },
            maxHeight: {
              xs: 32,
              sm: 36,
              md: 40,
            },
            p: 0,
            mb: 2,
            display: "flex",
            alignItems: "center",
          }}
          value={hierarchyOptions.includes(selectedHierarchy) ? selectedHierarchy : ""}
          onChange={(event) => {
            setSelectedHierarchy(event.target.value);
          }}
          options={hierarchyOptions}
        />
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <FacebookCircularProgress loading={true} hasData={true} />
        </Box>
      ) : error || !segmentData || segmentData.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <FacebookCircularProgress
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message={
              error
                ? `Error loading data: ${error} - Click to retry`
                : "No data available - Click to reset chart"
            }
          />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              overflowX: needsScroll ? "auto" : "hidden",
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
              minWidth: 0,
              mx: { xs: -1, sm: -1, md: 0 },
              px: { xs: 1, sm: 1, md: 0 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                gap: { xs: 0.5, sm: 0.25, md: 1, lg: 2 },
                pb: { xs: 3, sm: 4 },
                borderBottom: "1px solid #e0e0e0",
                minWidth: needsScroll ? scrollableMinWidth : 0,
                width: needsScroll ? "max-content" : "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minWidth: { xs: 32, sm: 40, md: 48, lg: 60 },
                  height: barAreaHeight + (isMobile ? 100 : 140),
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: "#fff",
                    borderRadius: 1,
                    px: { xs: 0.5, sm: 1 },
                    py: 1,
                    writingMode: "vertical-lr",
                    textOrientation: "mixed",
                    fontWeight: 600,
                    fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.8rem" },
                    textTransform: "uppercase",
                    transform: "rotate(180deg)",
                    height: barAreaHeight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  WOB% 2021 YTD
                </Box>
                <Box
                  sx={{
                    backgroundColor: theme.palette.secondary.dark,
                    color: "#fff",
                    borderRadius: 1,
                    px: { xs: 0.5, sm: 1 },
                    py: 1,
                    writingMode: "vertical-lr",
                    textOrientation: "mixed",
                    fontWeight: 600,
                    fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.8rem" },
                    textTransform: "uppercase",
                    transform: "rotate(180deg)",
                    display: "inline-block",
                    mt: { xs: 3, sm: 5 },
                  }}
                >
                  Contribution
                </Box>
              </Box>

              <Box
                sx={{
                  flex: needsScroll ? "0 0 auto" : 1,
                  display: "grid",
                  gridTemplateColumns: gridColumns,
                  gap: { xs: 0.25, sm: 0.25, md: 0.5, lg: 2 },
                  minWidth: needsScroll ? segmentData.length * mobileColWidth + 40 : 0,
                  width: needsScroll ? "max-content" : "100%",
                }}
              >
              {segmentData.map((segment) => {
                const barOffset = isMobile ? 20 : 30;
                const barHeight = Math.max(4, (segment.wob / maxWob) * barAreaHeight - barOffset);
                const contributionColor = getContributionColor(
                  segment.contribution
                );
                const labelOffset = segment.contribution < 0 ? 10 : isMobile ? -18 : isTablet ? -20 : -22;

                return (
                  <Box
                    key={segment.name}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: { xs: 1, sm: 0.75, md: 1, lg: 1.5 },
                      minHeight: barAreaHeight + (isMobile ? 100 : 140),
                      minWidth: 0,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: barAreaHeight,
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          position: "absolute",
                          bottom: `${barHeight + (isMobile ? 3 : 5)}px`,
                          left: "50%",
                          transform: "translateX(-50%)",
                          fontWeight: 700,
                          fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.75rem" },
                          color: theme.palette.secondary.dark,
                        }}
                      >
                        {segment.wob.toFixed(2)}%
                      </Typography>
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {segment.name}
                            </Typography>
                            <Typography variant="body2">
                              WOB%: {segment.wob.toFixed(2)}%
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <Box
                          sx={{
                            width: barWidth,
                            maxWidth: "100%",
                            height: `${barHeight}px`,
                            backgroundColor: "#d8d8d8",
                            borderRadius: "4px 4px 0 0",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </Box>

                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {segment.name}
                          </Typography>
                          <Typography variant="body2">
                            Contribution to Growth: {formatContribution(segment.contribution)}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          width: "70%",
                          height: 4,
                          borderRadius: 999,
                          backgroundColor: contributionColor,
                          position: "relative",
                          mt: 5,
                          cursor: "pointer",
                        }}
                      >
                        <Typography
                          sx={{
                            position: "absolute",
                            top: labelOffset,
                            left: "50%",
                            transform: "translateX(-50%)",
                            fontWeight: 600,
                            fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.75rem" },
                            color: contributionColor,
                          }}
                        >
                          {formatContribution(segment.contribution)}
                        </Typography>
                      </Box>
                    </Tooltip>

                    <Typography
                      sx={{
                        fontSize: { xs: "0.55rem", sm: "0.7rem", md: "0.75rem" },
                        fontWeight: 600,
                        textAlign: "center",
                        writingMode: "vertical-rl",
                        textOrientation: "mixed",
                        transform: "rotate(180deg)",
                        color: theme.palette.text.primary,
                        lineHeight: 1.2,
                        whiteSpace: "pre-line",
                        mt: { xs: 2, sm: 3 },
                        maxWidth: { xs: "26px", sm: "32px", md: "42px", lg: "60px" },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {segment.name.replace(/ /g, "\n")}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
          </Box>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              mt: { xs: 2, sm: 3 },
              backgroundColor: "transparent",
              overflowX: needsScroll ? "auto" : "hidden",
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
              width: "100%",
              minWidth: 0,
            }}
          >
            <Table 
              size="small"
              sx={{
                tableLayout: "fixed",
                width: "100%",
                minWidth: needsScroll ? `${segmentData.length * mobileColWidth + 64}px` : undefined,
              }}
            >
              <TableBody>
                {tableRows.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "0.58rem", sm: "0.85rem", md: "0.85rem" },
                        borderBottom: "none",
                        textTransform: "uppercase",
                        width: { xs: "8%", sm: "6%", md: "6%" },
                        px: { xs: 0.5, sm: 0.5, md: 0.75, lg: 1 },
                        ...row.leftCellStyles,
                      }}
                    >
                      {row.label}
                    </TableCell>
                    {segmentData.map((segment) => {
                      const cellColor = row.valueColor
                        ? row.valueColor(segment)
                        : row.cellStyles?.color || theme.palette.text.primary;

                      return (
                        <TableCell
                          key={`${row.label}-${segment.name}`}
                          align="center"
                          sx={{
                            borderBottom: "none",
                            fontWeight: 600,
                            fontSize: { xs: "0.58rem", sm: "0.85rem", md: "0.85rem" },
                            ...row.cellStyles,
                            color: cellColor,
                            px: { xs: 0.5, sm: 0.5, md: 0.75, lg: 1 },
                          }}
                        >
                          {row.getValue(segment)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

MarketContributionSegments.chartInfo = "IYA (Index Year Ago) Comparison vs. the same period last year";

export default MarketContributionSegments;

